import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePlayerStore } from '@/stores/playerStore';
import { useTeamStore } from '@/stores/teamStore';
import { useMatchStore } from '@/stores/matchStore';
import { useToast } from '@/hooks/use-toast';
import type { GameMode, TargetScore, Participant, MatchFormat } from '@/types';
import { GAME_MODE_CONFIG, MATCH_FORMAT_CONFIG } from '@/types';

type TeamSelectionMode = 'predefined' | 'custom';

export function NewMatchPage() {
  const navigate = useNavigate();
  const { players, loadPlayers } = usePlayerStore();
  const { teams, loadTeams } = useTeamStore();
  const { createMatch } = useMatchStore();
  const { toast } = useToast();

  const [format, setFormat] = useState<MatchFormat>('best_of_3');
  const [mode, setMode] = useState<GameMode>('4_player');
  const [targetScore, setTargetScore] = useState<TargetScore>(1001);
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [teamSelectionMode, setTeamSelectionMode] =
    useState<TeamSelectionMode>('predefined');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  useEffect(() => {
    loadPlayers();
    loadTeams();
  }, [loadPlayers, loadTeams]);

  // Update target score when mode changes
  useEffect(() => {
    const config = GAME_MODE_CONFIG[mode];
    setTargetScore(config.defaultTarget);
    setSelectedPlayers([]);
    setSelectedTeams([]);
  }, [mode]);

  const config = GAME_MODE_CONFIG[mode];

  const getRequiredPlayers = (): number => {
    if (mode === '2_player') return 2;
    if (mode === '3_player') return 3;
    return 4; // 4_player mode needs 4 players (2 teams)
  };

  const isValidCustomTeams =
    mode === '4_player' && selectedPlayers.length === 4;
  const isValidPredefinedTeams =
    mode === '4_player' && selectedTeams.length === 2;
  const isValidPlayers =
    mode !== '4_player' && selectedPlayers.length === getRequiredPlayers();

  const isValid =
    mode === '4_player'
      ? teamSelectionMode === 'predefined'
        ? isValidPredefinedTeams
        : isValidCustomTeams
      : isValidPlayers;

  const handlePlayerSelect = (index: number, playerId: string) => {
    const newSelection = [...selectedPlayers];
    newSelection[index] = playerId;
    setSelectedPlayers(newSelection);
  };

  const handleTeamSelect = (index: number, teamId: string) => {
    const newSelection = [...selectedTeams];
    newSelection[index] = teamId;
    setSelectedTeams(newSelection);
  };

  const handleStartMatch = async () => {
    if (!isValid) return;

    let participants: Participant[];

    if (mode === '4_player') {
      if (teamSelectionMode === 'predefined') {
        // Use predefined teams
        const team1 = teams.find((t) => t.id === selectedTeams[0])!;
        const team2 = teams.find((t) => t.id === selectedTeams[1])!;

        participants = [
          {
            id: crypto.randomUUID(),
            type: 'team',
            name: team1.name,
            playerIds: [team1.player1Id, team1.player2Id],
            teamId: team1.id,
          },
          {
            id: crypto.randomUUID(),
            type: 'team',
            name: team2.name,
            playerIds: [team2.player1Id, team2.player2Id],
            teamId: team2.id,
          },
        ];
      } else {
        // Create ad-hoc teams
        const team1Players = [
          players.find((p) => p.id === selectedPlayers[0])!,
          players.find((p) => p.id === selectedPlayers[1])!,
        ];
        const team2Players = [
          players.find((p) => p.id === selectedPlayers[2])!,
          players.find((p) => p.id === selectedPlayers[3])!,
        ];

        participants = [
          {
            id: crypto.randomUUID(),
            type: 'team',
            name: `${team1Players[0].name} & ${team1Players[1].name}`,
            playerIds: [team1Players[0].id, team1Players[1].id],
          },
          {
            id: crypto.randomUUID(),
            type: 'team',
            name: `${team2Players[0].name} & ${team2Players[1].name}`,
            playerIds: [team2Players[0].id, team2Players[1].id],
          },
        ];
      }
    } else {
      // Individual players
      participants = selectedPlayers.map((playerId) => {
        const player = players.find((p) => p.id === playerId)!;
        return {
          id: player.id,
          type: 'player' as const,
          name: player.name,
        };
      });
    }

    const match = await createMatch({
      format,
      mode,
      targetScore,
      participants,
    });

    if (match) {
      toast({
        title: 'Match started',
        description: `${MATCH_FORMAT_CONFIG[format].label} - Good luck!`,
      });
      navigate(`/matches/active/${match.id}`);
    }
  };

  const getAvailablePlayers = (currentIndex: number) => {
    return players.filter(
      (player) =>
        !selectedPlayers.includes(player.id) ||
        selectedPlayers[currentIndex] === player.id
    );
  };

  const getAvailableTeams = (currentIndex: number) => {
    return teams.filter(
      (team) =>
        !selectedTeams.includes(team.id) ||
        selectedTeams[currentIndex] === team.id
    );
  };

  return (
    <div className="container py-6 space-y-6">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/matches')}
        className="-ml-2"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Matches
      </Button>

      {/* Match Format Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Match Format</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(MATCH_FORMAT_CONFIG) as MatchFormat[]).map(
              (matchFormat) => {
                const formatConfig = MATCH_FORMAT_CONFIG[matchFormat];
                return (
                  <Button
                    key={matchFormat}
                    variant={format === matchFormat ? 'default' : 'outline'}
                    onClick={() => setFormat(matchFormat)}
                    className="h-auto py-3 flex flex-col"
                  >
                    <span className="text-lg font-bold">
                      {formatConfig.shortLabel}
                    </span>
                    <span className="text-xs opacity-80">
                      {formatConfig.gamesToWin} to win
                    </span>
                  </Button>
                );
              }
            )}
          </div>
        </CardContent>
      </Card>

      {/* Game Mode Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Game Mode</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(GAME_MODE_CONFIG) as GameMode[]).map((gameMode) => (
              <Button
                key={gameMode}
                variant={mode === gameMode ? 'default' : 'outline'}
                onClick={() => setMode(gameMode)}
                className="h-auto py-3 flex flex-col"
              >
                <span className="text-lg font-bold">
                  {gameMode === '2_player'
                    ? '2'
                    : gameMode === '3_player'
                      ? '3'
                      : '4'}
                </span>
                <span className="text-xs">Players</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Target Score Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Target Score (per game)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {config.allowedTargets.map((target) => (
              <Button
                key={target}
                variant={targetScore === target ? 'default' : 'outline'}
                onClick={() => setTargetScore(target)}
              >
                {target}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Team/Player Selection */}
      <Card>
        <CardHeader>
          <CardTitle>
            {mode === '4_player' ? 'Select Teams' : 'Select Players'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {mode === '4_player' && (
            <>
              {/* Team Selection Mode Toggle */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={
                    teamSelectionMode === 'predefined' ? 'default' : 'outline'
                  }
                  onClick={() => {
                    setTeamSelectionMode('predefined');
                    setSelectedPlayers([]);
                  }}
                  disabled={teams.length < 2}
                >
                  Saved Teams
                </Button>
                <Button
                  variant={
                    teamSelectionMode === 'custom' ? 'default' : 'outline'
                  }
                  onClick={() => {
                    setTeamSelectionMode('custom');
                    setSelectedTeams([]);
                  }}
                  disabled={players.length < 4}
                >
                  Custom Teams
                </Button>
              </div>

              {teamSelectionMode === 'predefined' ? (
                teams.length < 2 ? (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground mb-2">
                      You need at least 2 saved teams.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/teams')}
                    >
                      Create Teams
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[0, 1].map((index) => (
                      <div key={index} className="space-y-2">
                        <Label className="text-sm font-medium">
                          Team {index + 1}
                        </Label>
                        <Select
                          value={selectedTeams[index] || ''}
                          onValueChange={(value) =>
                            handleTeamSelect(index, value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={`Select team ${index + 1}`}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailableTeams(index).map((team) => (
                              <SelectItem key={team.id} value={team.id}>
                                {team.name} ({team.player1.name} &{' '}
                                {team.player2.name})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )
              ) : players.length < 4 ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-2">
                    You need at least 4 players.
                  </p>
                  <Button variant="outline" onClick={() => navigate('/players')}>
                    Add Players
                  </Button>
                </div>
              ) : (
                <>
                  {/* Team 1 */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Team 1</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[0, 1].map((index) => (
                        <Select
                          key={index}
                          value={selectedPlayers[index] || ''}
                          onValueChange={(value) =>
                            handlePlayerSelect(index, value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Player ${index + 1}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailablePlayers(index).map((player) => (
                              <SelectItem key={player.id} value={player.id}>
                                {player.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ))}
                    </div>
                  </div>

                  {/* Team 2 */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Team 2</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[2, 3].map((index) => (
                        <Select
                          key={index}
                          value={selectedPlayers[index] || ''}
                          onValueChange={(value) =>
                            handlePlayerSelect(index, value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Player ${index - 1}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailablePlayers(index).map((player) => (
                              <SelectItem key={player.id} value={player.id}>
                                {player.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {mode !== '4_player' && (
            <>
              {players.length < getRequiredPlayers() ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-2">
                    You need at least {getRequiredPlayers()} players.
                  </p>
                  <Button variant="outline" onClick={() => navigate('/players')}>
                    Add Players
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {Array.from({ length: getRequiredPlayers() }).map(
                    (_, index) => (
                      <Select
                        key={index}
                        value={selectedPlayers[index] || ''}
                        onValueChange={(value) =>
                          handlePlayerSelect(index, value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={`Select Player ${index + 1}`}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailablePlayers(index).map((player) => (
                            <SelectItem key={player.id} value={player.id}>
                              {player.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Start Match Button */}
      <Button
        size="lg"
        className="w-full h-14 text-lg"
        disabled={!isValid}
        onClick={handleStartMatch}
      >
        Start Match
      </Button>
    </div>
  );
}
